import { ApiError } from '@echo/api-client/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { isRetryable, OnEarlyRequestError } from '../content/errors';
import { weekStartOf } from '../content/group-progress';
import {
  attemptScope,
  flushOutbox,
  joinRunning,
  ownedBy,
  withoutProcessed,
} from '../content/outbox';
import { ensurePhotoUploaded } from '../content/photo';
import { scopeKey, type WriteScope } from '../content/scope';
import { loadSessionIndex, loadSessions, recordAttempt } from '../content/sessions';
import { deriveSummary, writeSummary } from '../content/summary';
import { queryKeys } from '../query/keys';
import { exerciseMinutesGeneration } from './exercise-minutes-store';
import { getOutbox, replaceOutbox } from './outbox-store';
import { useConfig } from './provider';
import { useWriteScope } from './use-global-group';
import { marksForSummary } from './use-marks';
import { useMe } from './use-me';
import { todayIso } from './use-today';

/** A day's session row is per scope: an attempt joins only a row written the same way. */
const rowKey = (date: string, scope: WriteScope) => `${date}|${scopeKey(scope)}`;

/** The session that started the flush has ended; what is left waits for the next one. */
class SessionEndedError extends Error {}

/**
 * Stops a flush where a failed save need not be the attempt's fault: the network, or a 401 —
 * the session lacked a valid token, which says nothing about the attempt. Anything else Core
 * rejects is dropped so it cannot block the ones behind it.
 */
const stopsFlush = (error: unknown) =>
  isRetryable(error) ||
  ((error instanceof ApiError || error instanceof OnEarlyRequestError) && error.status === 401);

type FlushResult = { sent: number; failed: boolean };

/** One flush at a time across the app: the quiet retry and "Try again" join the same run. */
const joinFlush = joinRunning<FlushResult>();

/**
 * Sends the current person's exercises that never reached Core, in order (another account's
 * stay queued for when it signs back in): "Try again" and the quiet retries.
 * It asks Core only which session each day already has (`loadSessionIndex`) and learns the
 * sessions it creates from the saves themselves, so a retry costs little more than the saves;
 * the full history is refreshed afterwards, best effort, once something got through. Safe to
 * call with an empty outbox.
 */
export function useFlushOutbox() {
  const queryClient = useQueryClient();
  const { strongerMemoryTypes: types, strongerMemoryGroupActorUuid: groupActorUuid } = useConfig();
  const me = useMe();
  const writeScope = useWriteScope();

  const mutation = useMutation({
    mutationFn: () =>
      joinFlush(async (): Promise<FlushResult> => {
        const waiting = ownedBy(getOutbox(), me.data?.uuid).filter(
          (item) => item.sentAt === undefined,
        );
        // `exerciseMinutesGeneration` changes each time a session ends (`EchoProvider`).
        const session = exerciseMinutesGeneration();
        if (!types || waiting.length === 0) return { sent: 0, failed: false };
        const current = await writeScope();
        const sessionOf = new Map(
          (await loadSessionIndex(types)).flatMap((session) =>
            session.writtenAs
              ? [
                  [
                    rowKey(session.date, session.writtenAs),
                    { uuid: session.uuid, lineageUuid: session.lineageUuid as string | undefined },
                  ] as const,
                ]
              : [],
          ),
        );
        const recordedAt = new Map<string, number>();
        const result = await flushOutbox(
          waiting,
          async (item) => {
            if (exerciseMinutesGeneration() !== session) throw new SessionEndedError();
            // A failure after the session ended says nothing about the attempt: later calls ran with
            // another person's token, or none. One Core recorded stays sent, or it would be sent twice.
            try {
              const scope = attemptScope(item.input, current);
              const row = rowKey(item.date, scope);
              const input = await ensurePhotoUploaded(item.input, {
                subjectUuid: me.data?.uuid,
                scope,
                sessionLineageUuid: sessionOf.get(row)?.lineageUuid,
              });
              const recorded = await recordAttempt(types, {
                ...input,
                date: item.date,
                timeZone: item.timeZone,
                groupActorUuid,
                scope,
                subjectUuid: me.data?.uuid,
                sessionUuid: sessionOf.get(row)?.uuid,
                sessionLineageUuid: sessionOf.get(row)?.lineageUuid,
              });
              recordedAt.set(item.id, Date.now());
              // The next exercise of the same day, in the same scope, joins the row this save created.
              if (recorded.sessionUuid) {
                sessionOf.set(row, {
                  uuid: recorded.sessionUuid,
                  lineageUuid: recorded.sessionLineageUuid,
                });
              }
            } catch (error) {
              if (exerciseMinutesGeneration() !== session) throw new SessionEndedError();
              throw error;
            }
          },
          stopsFlush,
        );
        const sameSession = () => exerciseMinutesGeneration() === session;
        // What Core recorded stays in the outbox, marked, so it counts locally until Core's
        // history shows it (`useLocalSessions`); the sessions cache holds only what Core returned.
        replaceOutbox(withoutProcessed(getOutbox(), waiting, result.remaining, recordedAt));
        if (result.sent > 0 && sameSession()) {
          // Derived data: the exercises are saved whatever happens here. Each step after an
          // await checks the session again: the person may have signed out meanwhile.
          try {
            const sessions = await loadSessions(types);
            if (!sameSession()) return { sent: result.sent, failed: result.failed };
            queryClient.setQueryData(queryKeys.sessions(), sessions);
            const today = todayIso();
            const marks = await marksForSummary(
              queryClient,
              { strongerMemoryTypes: types, strongerMemoryGroupActorUuid: groupActorUuid },
              me.data?.uuid,
            );
            if (!sameSession()) return { sent: result.sent, failed: result.failed };
            await writeSummary(
              types,
              deriveSummary(sessions, today, groupActorUuid, marks),
              current,
              me.data?.uuid,
            );
            if (!sameSession()) return { sent: result.sent, failed: result.failed };
            await queryClient.invalidateQueries({
              queryKey: queryKeys.groupProgress(weekStartOf(today)),
            });
            if (!sameSession()) return { sent: result.sent, failed: result.failed };
            await queryClient.invalidateQueries({ queryKey: queryKeys.groupDashboards() });
          } catch {
            // The next completion, or the next load of Home, recomputes it.
          }
        }
        return { sent: result.sent, failed: result.failed };
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.sessions() }),
  });

  const flush = useCallback(() => mutation.mutateAsync(), [mutation.mutateAsync]);
  return { flush, isFlushing: mutation.isPending };
}
